import {
  List,
  TextField,
  FunctionField,
  DatagridConfigurable,
  Pagination,
  NumberField,
  useListContext,
} from 'react-admin';
import ClaimExceptionsFilters from './ClaimExceptionsFilters';
import { Tooltip, Typography, Box } from '@mui/material';
import GenericTableSkeleton from '../generic-table-skeleton/GenericTableSkeleton';

const formatDate = (timestamp?: string | number | null) => {
  if (!timestamp && timestamp !== 0) return '';
  const n = Number(timestamp);
  if (!Number.isFinite(n)) return String(timestamp ?? '');
  const d = new Date(n);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const Money = (props: Omit<React.ComponentProps<typeof NumberField>, 'options'>) => (
  <NumberField {...props} options={{ style: 'currency', currency: 'USD' }} />
);

const datagridStyles = {
  maxWidth: '90vw !important',
  '& .MuiTableHead-root': {
    position: 'sticky !important',
    top: '0 !important',
    zIndex: '10 !important',
    bgcolor: 'background.paper !important',
  },
  '& .MuiTableCell-head': {
    whiteSpace: 'nowrap !important',
    px: '16px !important',
    bgcolor: 'background.paper !important',
    borderBottom: '1px solid rgba(224, 224, 224, 1) !important',
  },
  '& .MuiTableCell-body': {
    whiteSpace: 'nowrap !important',
    px: '16px !important',
    py: '9.6px !important',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis !important',
    fontSize: '0.875rem !important',
  },
  '& .column-client_number, & .column-client_name, & .column-member_name': {
    textAlign: 'left !important',
  },
  '& .column-client_total_cost, & .column-fill_date': {
    textAlign: 'center !important',
  },
  '& .commentCol': {
    width: '400px !important',
    minWidth: '300px !important',
    maxWidth: '30vw !important',
    whiteSpace: 'nowrap !important',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis !important',
  },
};

const ClaimExceptionsTable = () => {
  const { isLoading } = useListContext();

  if (isLoading) {
    return <GenericTableSkeleton rows={10}>{Array(20).fill(<div />)}</GenericTableSkeleton>;
  }

  return (
    <DatagridConfigurable bulkActionButtons={false} size="small" sx={datagridStyles}>
      <TextField source="client_number" label="Client #" sortable={false} sx={{ width: '120 !important' }} />
      <TextField source="client_name" label="Client" sortable={false} sx={{ width: '180 !important' }} />
      <TextField source="member_id" label="Member ID" sortable={false} sx={{ width: '160 !important' }} />
      <TextField source="submitted_card_id" label="Card ID" sortable={false} sx={{ width: '170 !important' }} />
      <TextField source="member_name" label="Member Name" sortable={false} sx={{ width: '220 !important' }} />
      <FunctionField
        label="DOB"
        sortable={false}
        render={(r: any) => formatDate(r.member_dob)}
        sx={{ width: '110 !important' }}
      />
      <FunctionField
        label="Fill Date"
        sortable={false}
        render={(r: any) => formatDate(r.fill_date)}
        sx={{ width: '110 !important' }}
      />
      <TextField source="claim_id" label="Claim ID" sortable={false} sx={{ width: '170 !important' }} />
      <TextField
        source="reversed_claim_id"
        label="Reversed Claim ID"
        sortable={false}
        sx={{ width: '190 !important' }}
      />
      <TextField source="prescriber_npi" label="Prescriber NPI" sortable={false} sx={{ width: '170 !important' }} />
      <TextField source="rx_claim_number" label="RX Claim #" sortable={false} sx={{ width: '170 !important' }} />
      <TextField source="ndc" label="NDC" sortable={false} sx={{ width: '160 !important' }} />
      <TextField source="drug_name" label="Drug Name" sortable={false} sx={{ width: '200 !important' }} />
      <TextField source="strength" label="Strength" sortable={false} sx={{ width: '140 !important' }} />
      <Money source="client_total_cost" label="Total Cost" sortable={false} sx={{ width: '140 !important' }} />
      <Money source="client_member_copay" label="Member Copay" sortable={false} sx={{ width: '140 !important' }} />
      <Money source="client_plan_paid" label="Plan Paid" sortable={false} sx={{ width: '140 !important' }} />
      <TextField source="transaction_status" label="Status" sortable={false} sx={{ width: '110 !important' }} />
      <FunctionField
        label="Financial Cutoff"
        sortable={false}
        render={(r: any) => formatDate(r.financial_cutoff_date)}
        sx={{ width: '160 !important' }}
      />
      <FunctionField
        label="Comment"
        sortable={false}
        headerClassName="commentCol"
        cellClassName="commentCol"
        sx={{ minWidth: '250 !important' }}
        render={(record: any) => {
          const text = record.exception_comment || '';
          return (
            <Tooltip title={text} placement="top-start" arrow>
              <Typography variant="body2" noWrap sx={{ maxWidth: '250 !important', fontSize: 'inherit !important' }}>
                {text}
              </Typography>
            </Tooltip>
          );
        }}
      />
    </DatagridConfigurable>
  );
};

const ClaimExceptions = () => {
  return (
    <List
      title="Claim Exceptions"
      perPage={10}
      pagination={<Pagination />}
      resource="ClaimExceptions"
      filters={<ClaimExceptionsFilters />}
      actions={false}
      sx={{
        width: '100% !important',
        '& .RaList-content': {
          overflow: 'hidden !important',
          boxShadow: 'none !important',
          width: '100% !important',
        },
      }}
      empty={false}
    >
      <Box
        sx={{
          overflowX: 'auto !important',
          overflowY: 'hidden !important',
          bgcolor: 'background.paper !important',
          width: '100% !important',
          borderBottom: '1px solid #e0e0e0 !important',
          display: 'block !important',
        }}
      >
        <ClaimExceptionsTable />
      </Box>
    </List>
  );
};

export default ClaimExceptions;
